import type { Choice } from "@memorang/types/src/session/new-session";

import {
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	type Modifier,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from "@dnd-kit/core";

import {
	SortableContext,
	arrayMove,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Box } from "@memorang/ui";
import { SortableItem } from "../SortableItem";

export type DraggableListProps = {
	items: Choice[];
	onReorder: (newItems: Choice[]) => void;
	renderItem: (
		item: Choice,
		index: number,
		drag: () => void,
	) => React.ReactNode;
	answered?: boolean;
};

export const DraggableList = ({
	items,
	onReorder,
	renderItem,
	answered,
}: DraggableListProps) => {
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	// Custom modifier to restrict dragging within container bounds
	const restrictToVerticalAxis: Modifier = ({ transform }) => {
		return {
			...transform,
			x: 0, // Prevent horizontal movement
		};
	};

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (over && active.id !== over.id) {
			const oldIndex = items.findIndex((item) => item.id === active.id);
			const newIndex = items.findIndex((item) => item.id === over.id);
			const newItems = arrayMove(items, oldIndex, newIndex);
			onReorder(newItems);
		}
	};

	return (
		<Box gap={16}>
			{answered ? (
				<>
					{items.map((item, index) => (
						<Box key={item.id}>{renderItem(item, index, () => {})}</Box>
					))}
				</>
			) : (
				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					onDragEnd={handleDragEnd}
					modifiers={[restrictToVerticalAxis]}
				>
					<SortableContext
						items={items.map((item) => item.id)}
						strategy={verticalListSortingStrategy}
					>
						<Box gap={16}>
							{items.map((item, index) => (
								<SortableItem
									key={item.id}
									id={item.id}
									index={index}
									item={item}
									renderItem={renderItem}
								/>
							))}
						</Box>
					</SortableContext>
				</DndContext>
			)}
		</Box>
	);
};
